import {Trailer} from "../../../../../models/trailer";
import {Observable, Subscription} from "rxjs";
import {Carrier} from "@shared/models/carrier";
import {Customer, CustomerAssignmentSummary} from "@shared/models/customer";
import {Shift} from "@shared/models/shift";
import {ShiftContext} from "../../../../../models/shift-context";
import {Product} from "@shared/models/product";
import {SiteSubinventory} from "@shared/models/subinventory";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {OutboundLoadCreate} from "../../../../../models/outbound-load";
import {CustomerSite} from "@shared/models/customer-site";
import {fromFormControl} from "@shared/helpers/from-form-control";
import {SelectItem} from "primeng/api";

/**
 * Base class for outbound load template definitions.
 */
export abstract class BaseOutboundLoadTemplateDefinition {
    protected subscriptions: Subscription[] = [];

    unsubscribe() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions = [];
    }

    toFormGroup(): FormGroup<OutboundLoadTemplateFormControls> {
        const group = new FormGroup({
            // NOTE(nicholas-ramsey): The load date is always based on the shift context. We'll want to show it (generally), but users should never be able to edit it. So disabling it.
            loadDate: new FormControl<Date>({ value: null, disabled: true }, [Validators.required]),
            loadTime: new FormControl<string>(null, [Validators.required]),
            trailerId: new FormControl<Trailer['id']>(null, [Validators.required]),
            carrier: new FormControl<Carrier>(null, [Validators.required]),
            shipToLocation: new FormControl<CustomerSite>(null, [Validators.required]),
            invoiceTo: new FormControl<Customer>(null, [Validators.required]),
            pooler: new FormControl<Customer>(null, [Validators.required]),
            product: new FormControl<Product>(null, [Validators.required]),
            quantity: new FormControl<number>(null, [Validators.required]),
            hasShipped: new FormControl<boolean>(false),
            propakBOLNumber: new FormControl<string>('', [Validators.required]),
            scheduledPickupDate: new FormControl<Date | null>(null),
            scheduledPickupTime: new FormControl<string | null>(''),
            dateShipped: new FormControl<Date | null>(null),
            timeShipped: new FormControl<string | null>(''),
            shippedShiftId: new FormControl<Shift['id'] | null>(null),
            siteSubinventory: new FormControl<SiteSubinventory | null>(null)
        });
        const requireIfShipped = (field: keyof OutboundLoadTemplateFormControls) => {
            const control = group.get(field);
            if (!control) {
                return;
            }

            control.setValidators([Validators.required]);
            control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        };
        const clearIfNotShipped = (field: keyof OutboundLoadTemplateFormControls) => {
            const control = group.get(field);
            if (!control) {
                return;
            }

            control.clearValidators();
            control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        };

        this.subscriptions.push(
            fromFormControl(group.get('hasShipped'))
                .subscribe((hasShipped: boolean) => {
                    const fields: (keyof OutboundLoadTemplateFormControls)[] = [
                        'dateShipped',
                        'timeShipped',
                        'scheduledPickupDate',
                        'scheduledPickupTime',
                        'shippedShiftId'
                    ];

                    for (const field of fields) {
                        hasShipped ? requireIfShipped(field) : clearIfNotShipped(field);
                    }
        }));

        return group;
    }
}

export interface OutboundLoadTemplateField<T, TContext> {
    /**
     * Function to provide a default value for the field based on the context.
     */
    defaultValueFn?: (context: TContext) => T | Observable<T>;
    /**
     * Indicates whether this field can be visible to the user (hides from table otherwise).
     */
    isVisible?: boolean;
}

/**
 * Context for each outbound load row instance.
 */
export interface OutboundLoadTemplateLocalContext {
    products: Product[];
    shipToLocationOptions: SelectItem<CustomerSite>[];
    poolers: Customer[];
    customers: Customer[];
}

/**
 * Context shared across all outbound load rows.
 */
export interface OutboundLoadTemplateSharedContext {
    shiftContext: ShiftContext;
    siteTimezone: string | null;
    carriers: Carrier[];
    siteSubInventories: SiteSubinventory[];
    customerAssignments: CustomerAssignmentSummary;
}

export interface OutboundLoadTemplate {
    loadDate: OutboundLoadTemplateField<Date, OutboundLoadTemplateSharedContext>;
    loadTime: OutboundLoadTemplateField<string, OutboundLoadTemplateSharedContext>;
    trailerId: OutboundLoadTemplateField<Trailer['id'], OutboundLoadTemplateSharedContext>;
    carrier: OutboundLoadTemplateField<Observable<Carrier>, OutboundLoadTemplateSharedContext>;
    shipToLocation: OutboundLoadTemplateField<CustomerSite, OutboundLoadTemplateSharedContext>;
    invoiceTo: OutboundLoadTemplateField<Observable<Customer>, OutboundLoadTemplateSharedContext>;
    pooler: OutboundLoadTemplateField<Observable<Customer>, OutboundLoadTemplateSharedContext>;
    product: OutboundLoadTemplateField<Product, OutboundLoadTemplateSharedContext>;
    quantity: OutboundLoadTemplateField<number, OutboundLoadTemplateSharedContext>;
    hasShipped: OutboundLoadTemplateField<boolean, OutboundLoadTemplateSharedContext>;
    propakBOLNumber: OutboundLoadTemplateField<string, OutboundLoadTemplateSharedContext>;
    scheduledPickupDate: OutboundLoadTemplateField<Date, OutboundLoadTemplateSharedContext>;
    scheduledPickupTime: OutboundLoadTemplateField<string, OutboundLoadTemplateSharedContext>;
    dateShipped: OutboundLoadTemplateField<Date, OutboundLoadTemplateSharedContext>;
    timeShipped: OutboundLoadTemplateField<string, OutboundLoadTemplateSharedContext>;
    shippedShiftId: OutboundLoadTemplateField<Shift['id'], OutboundLoadTemplateSharedContext>;
    siteSubinventory: OutboundLoadTemplateField<SiteSubinventory, OutboundLoadTemplateSharedContext>;
}

export interface OutboundLoadTemplateFormControls {
    loadDate: FormControl<Date>;
    loadTime: FormControl<string>;
    trailerId: FormControl<Trailer['id']>;
    carrier: FormControl<Carrier>;
    shipToLocation: FormControl<CustomerSite>;
    invoiceTo: FormControl<Customer>;
    pooler: FormControl<Customer>;
    product: FormControl<Product>;
    quantity: FormControl<number>;
    hasShipped: FormControl<boolean>;
    propakBOLNumber: FormControl<string>;
    scheduledPickupDate: FormControl<Date>;
    scheduledPickupTime: FormControl<string>;
    dateShipped: FormControl<Date>;
    timeShipped: FormControl<string>;
    shippedShiftId: FormControl<Shift['id']>;
    siteSubinventory: FormControl<SiteSubinventory>;
}

export interface OutboundLoadTemplateDefinition {
    label: string;
    template: OutboundLoadTemplate;
    unsubscribe(): void;
    transformContext?(context: OutboundLoadTemplateLocalContext): OutboundLoadTemplateLocalContext;

    /**
     * Transforms the form group into the OutboundLoad create payload.
     * @param formGroup
     * @param sharedContext
     * @param localContext
     */
    toOutboundLoadModel(formGroup: FormGroup<OutboundLoadTemplateFormControls>, sharedContext: OutboundLoadTemplateSharedContext, localContext: OutboundLoadTemplateLocalContext): OutboundLoadCreate;

    /**
     * Converts the template to a form group
     */
    toFormGroup(): FormGroup<OutboundLoadTemplateFormControls>;
}
